import { lazy, Suspense } from "react";
import { Routes, Route, Navigate, Outlet } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Placeholder } from "@/components/Placeholder";
import { LoginPage } from "@/features/auth/LoginPage";
import { MyShiftsPage } from "@/features/shifts/MyShiftsPage";
import { MyLeavesPage } from "@/features/leaves/MyLeavesPage";
import { NewLeavePage } from "@/features/leaves/NewLeavePage";
import { MyDocumentsPage } from "@/features/documents/MyDocumentsPage";
import { DocumentViewPage } from "@/features/documents/DocumentViewPage";
import { MyTasksPage } from "@/features/tasks/MyTasksPage";
import { TaskDetailPage } from "@/features/tasks/TaskDetailPage";
import { NotificationsPage } from "@/features/notifications/NotificationsPage";
import { ProfilePage } from "@/features/profile/ProfilePage";

// Admin + legal pages are lazy-loaded: only admins/superadmins hit most of
// these, and the legal pages are rarely visited, so they don't belong in
// the main bundle that every employee downloads on login.
const AdminUsersListPage = lazy(() => import("@/features/admin/users/AdminUsersListPage").then(m => ({ default: m.AdminUsersListPage })));
const AdminNewUserPage = lazy(() => import("@/features/admin/users/AdminNewUserPage").then(m => ({ default: m.AdminNewUserPage })));
const AdminPasswordsPage = lazy(() => import("@/features/admin/users/AdminPasswordsPage").then(m => ({ default: m.AdminPasswordsPage })));
const AdminShiftsPage = lazy(() => import("@/features/admin/shifts/AdminShiftsPage").then(m => ({ default: m.AdminShiftsPage })));
const AdminSettingsPage = lazy(() => import("@/features/admin/settings/AdminSettingsPage").then(m => ({ default: m.AdminSettingsPage })));
const AdminWorkerTypesPage = lazy(() => import("@/features/admin/worker-types/AdminWorkerTypesPage").then(m => ({ default: m.AdminWorkerTypesPage })));
const AdminHolidaysPage = lazy(() => import("@/features/admin/holidays/AdminHolidaysPage").then(m => ({ default: m.AdminHolidaysPage })));
const AdminSchedulesPage = lazy(() => import("@/features/admin/schedules/AdminSchedulesPage").then(m => ({ default: m.AdminSchedulesPage })));
const AdminLeavesPage = lazy(() => import("@/features/admin/leaves/AdminLeavesPage").then(m => ({ default: m.AdminLeavesPage })));
const AdminAbsencesPage = lazy(() => import("@/features/admin/absences/AdminAbsencesPage").then(m => ({ default: m.AdminAbsencesPage })));
const AdminSickLeavesPage = lazy(() => import("@/features/admin/sick-leaves/AdminSickLeavesPage").then(m => ({ default: m.AdminSickLeavesPage })));
const AdminTasksPage = lazy(() => import("@/features/admin/tasks/AdminTasksPage").then(m => ({ default: m.AdminTasksPage })));
const AdminTaskFormPage = lazy(() => import("@/features/admin/tasks/AdminTaskFormPage").then(m => ({ default: m.AdminTaskFormPage })));
const AdminDocumentsPage = lazy(() => import("@/features/admin/documents/AdminDocumentsPage").then(m => ({ default: m.AdminDocumentsPage })));
const AdminTimelinePage = lazy(() => import("@/features/admin/timeline/AdminTimelinePage"));
const AdminShiftEventsPage = lazy(() => import("@/features/admin/shift-events/AdminShiftEventsPage"));
const AdminSummaryPage = lazy(() => import("@/features/admin/summary/AdminSummaryPage"));
const AdminTabletsPage = lazy(() => import("@/features/admin/tablets/AdminTabletsPage"));
const AdminLocationsPage = lazy(() => import("@/features/admin/locations/AdminLocationsPage"));
const AdminPermissionsPage = lazy(() => import("@/features/admin/permissions/AdminPermissionsPage"));
const AdminAutoRulesPage = lazy(() => import("@/features/admin/auto-rules/AdminAutoRulesPage"));
const AdminRegistroLegalPage = lazy(() => import("@/features/admin/registro-legal/AdminRegistroLegalPage"));
const AdminFaltasPage = lazy(() => import("@/features/admin/faltas/AdminFaltasPage"));
const AdminFeaturesPage = lazy(() => import("@/features/admin/features/AdminFeaturesPage"));
const AdminTeamCalendarPage = lazy(() => import("@/features/admin/team-calendar/AdminTeamCalendarPage"));
const AdminUserContractPage = lazy(() => import("@/features/admin/users/AdminUserContractPage"));
const AdminUsersManagePage = lazy(() => import("@/features/admin/users/AdminUsersManagePage"));
const LegalTermsPage = lazy(() => import("@/features/legal/LegalTermsPage"));
const LegalPrivacyPage = lazy(() => import("@/features/legal/LegalPrivacyPage"));
const LegalCookiesPage = lazy(() => import("@/features/legal/LegalCookiesPage"));

function RouteFallback() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center text-muted-foreground">
      Loading…
    </div>
  );
}

export function AppRoutes() {
  return (
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      {/* Single shell for everything (matches base.html behaviour) */}
      <Route element={<AppLayout />}>
        {/* Public */}
        <Route path="/auth/login" element={<LoginPage />} />
        <Route path="/legal/terms" element={<LegalTermsPage />} />
        <Route path="/legal/privacy" element={<LegalPrivacyPage />} />
        <Route path="/legal/cookies" element={<LegalCookiesPage />} />

        {/* Authenticated */}
        <Route
          element={
            <ProtectedRoute>
              <Outlet />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/shifts/my" replace />} />

          {/* Employee */}
          <Route path="/shifts/my" element={<MyShiftsPage />} />
          <Route path="/leaves/my" element={<MyLeavesPage />} />
          <Route path="/leaves/new" element={<NewLeavePage />} />
          <Route path="/documents" element={<MyDocumentsPage />} />
          <Route path="/documents/:id" element={<DocumentViewPage />} />
          <Route path="/tasks/my" element={<MyTasksPage />} />
          <Route path="/tasks/:id" element={<TaskDetailPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/profile" element={<ProfilePage />} />

          {/* Admin */}
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute requireAdmin permission="ADMIN_USERS">
                <AdminUsersListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users/new"
            element={
              <ProtectedRoute requireAdmin permission="ADMIN_USERS">
                <AdminNewUserPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/passwords"
            element={
              <ProtectedRoute requireAdmin permission="ADMIN_PASSWORD_RESET">
                <AdminPasswordsPage />
              </ProtectedRoute>
            }
          />
          <Route path="/admin/settings"
            element={
              <ProtectedRoute requireAdmin permission="ADMIN_USERS">
                <AdminSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/shifts"
            element={
              <ProtectedRoute requireAdmin permission="ADMIN_SHIFTS">
                <AdminShiftsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/worker-types"
            element={
              <ProtectedRoute requireAdmin permission="ADMIN_USERS">
                <AdminWorkerTypesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/holidays"
            element={
              <ProtectedRoute requireAdmin permission="ADMIN_USERS">
                <AdminHolidaysPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/schedules"
            element={
              <ProtectedRoute requireAdmin permission="ADMIN_SCHEDULES">
                <AdminSchedulesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/leaves"
            element={
              <ProtectedRoute requireAdmin permission="ADMIN_LEAVES">
                <AdminLeavesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/absences"
            element={
              <ProtectedRoute requireAdmin permission="ADMIN_ABSENCES">
                <AdminAbsencesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/sick-leaves"
            element={
              <ProtectedRoute requireAdmin permission="ADMIN_SICK_LEAVES">
                <AdminSickLeavesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/tasks"
            element={
              <ProtectedRoute requireAdmin permission="ADMIN_TASKS">
                <AdminTasksPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/tasks/new"
            element={
              <ProtectedRoute requireAdmin permission="ADMIN_TASKS">
                <AdminTaskFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/tasks/:id/edit"
            element={
              <ProtectedRoute requireAdmin permission="ADMIN_TASKS">
                <AdminTaskFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/documents"
            element={
              <ProtectedRoute requireAdmin permission="ADMIN_DOCUMENTS">
                <AdminDocumentsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/timeline"
            element={
              <ProtectedRoute requireAdmin permission="ADMIN_SUMMARY_WEEKLY">
                <AdminTimelinePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/shift-events"
            element={
              <ProtectedRoute requireAdmin permission="ADMIN_SHIFTS">
                <AdminShiftEventsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/summary"
            element={
              <ProtectedRoute requireAdmin permission="ADMIN_SUMMARY_WEEKLY">
                <AdminSummaryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/summary/monthly"
            element={
              <ProtectedRoute requireAdmin permission="ADMIN_SUMMARY_MONTHLY">
                <Navigate to="/admin/summary?view=monthly" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/summary/daily"
            element={
              <ProtectedRoute requireAdmin permission="ADMIN_SUMMARY_WEEKLY">
                <Navigate to="/admin/summary?view=daily" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/tablets"
            element={
              <ProtectedRoute requireAdmin permission="TABLET_MANAGE_DEVICES">
                <AdminTabletsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/locations"
            element={
              <ProtectedRoute requireAdmin permission="TABLET_MANAGE_DEVICES">
                <AdminLocationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/permissions"
            element={
              <ProtectedRoute requireSuperadmin>
                <AdminPermissionsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/auto-rules"
            element={
              <ProtectedRoute requireAdmin permission="ADMIN_AUTOSHIFT">
                <AdminAutoRulesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/registro-legal"
            element={
              <ProtectedRoute requireAdmin permission="ADMIN_SUMMARY_WEEKLY">
                <AdminRegistroLegalPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/faltas"
            element={
              <ProtectedRoute requireAdmin permission="MANAGE_INFRACTIONS">
                <AdminFaltasPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/features"
            element={
              <ProtectedRoute requireSuperadmin>
                <AdminFeaturesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/team-calendar"
            element={
              <ProtectedRoute requireAdmin permission="ADMIN_USERS">
                <AdminTeamCalendarPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users/manage"
            element={
              <ProtectedRoute requireSuperadmin>
                <AdminUsersManagePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users/:userId/contract"
            element={
              <ProtectedRoute requireAdmin permission="ADMIN_USERS">
                <AdminUserContractPage />
              </ProtectedRoute>
            }
          />
          <Route path="/admin/*" element={<Placeholder title="Admin (WIP)" />} />
        </Route>

        {/* 404 */}
        <Route
          path="*"
          element={
            <div style={{ padding: "4rem 0", textAlign: "center" }}>
              <h1>404</h1>
              <p>Page not found</p>
            </div>
          }
        />
      </Route>
    </Routes>
    </Suspense>
  );
}
